const INCH_TO_MM = 25.4;

const morse = [
  ["MT0", 9.045, 56.5, 19.212],
  ["MT1", 12.065, 62, 20.047],
  ["MT2", 17.78, 75, 20.02],
  ["MT3", 23.825, 94, 19.922],
  ["MT4", 31.267, 117.5, 19.254],
  ["MT4.5", 37.833, 133.5, 19.128],
  ["MT5", 44.399, 149.5, 19.002],
  ["MT6", 63.348, 210, 19.18],
  ["MT7", 83.058, 285.75, 19.231],
].map(([size, largeDiameterMm, referenceLengthMm, taperRatio]) => ({
  id: size.toLowerCase().replace(".", "-"),
  family: "morse",
  size,
  largeDiameterIn: largeDiameterMm / INCH_TO_MM,
  smallDiameterIn: (largeDiameterMm - (referenceLengthMm / taperRatio)) / INCH_TO_MM,
  referenceLengthIn: referenceLengthMm / INCH_TO_MM,
  taperPerInch: 1 / taperRatio,
  source: "ISO 296 / DIN 228 nominal tang-type geometry reference",
  notes: "Morse taper dimensions vary by shank form and end style. Confirm the applicable standard drawing and gauge limits before producing a mating tool surface.",
}));

const steepTaper = (size) => ({
  id: `steep-${size}`,
  family: "steep-724",
  size,
  taperPerInch: 7 / 24,
  source: "ANSI/ASME B5.50 and ISO 7388 7:24 basic taper geometry",
  notes: "CAT, BT, and NMTB share the 7:24 basic taper angle, but flange, gauge line, keyway, retention, and pull-stud details are not interchangeable. Enter drawing diameters for cutting work.",
  requiresDiameter: true,
});

export const TAPER_FAMILIES = [
  { id: "custom", label: "Generic Custom Taper" },
  { id: "morse", label: "Morse Taper" },
  { id: "r8", label: "R8 Taper" },
  { id: "jacobs", label: "Jacobs Taper" },
  { id: "brown-sharpe", label: "Brown & Sharpe Taper" },
  { id: "steep-724", label: "NMTB / CAT / BT 7:24 Reference" },
];

export const TAPER_REFERENCES = [
  ...morse,
  {
    id: "r8",
    family: "r8",
    size: "R8",
    largeDiameterIn: 1.25,
    taperPerInch: 2 * Math.tan((16.85 / 2) * (Math.PI / 180)),
    source: "Bridgeport R8 nominal 1.250 in major diameter and 16 deg 51 min included angle reference",
    notes: "This entry supplies basic taper geometry only. R8 includes straight pilot, keyway, thread, and gauge relationships that must come from the applicable spindle/tool drawing.",
    requiresLength: true,
  },
  steepTaper("30"),
  steepTaper("40"),
  steepTaper("45"),
  steepTaper("50"),
  {
    id: "jacobs-custom",
    family: "jacobs",
    size: "Drawing / Measured Jacobs",
    source: "User-entered drawing or measured dimensions",
    notes: "Jacobs taper rates vary by size. Enter the major diameter, minor diameter, and reference length from the chuck/arbor manufacturer's drawing.",
    requiresDiameter: true,
  },
  {
    id: "brown-sharpe-custom",
    family: "brown-sharpe",
    size: "Drawing / Measured Brown & Sharpe",
    source: "User-entered drawing or measured dimensions",
    notes: "Brown & Sharpe sizes are not represented by one exact universal diameter set. Enter dimensions from the applicable standard or machine drawing.",
    requiresDiameter: true,
  },
];

export const getTaperReferences = (family) =>
  TAPER_REFERENCES.filter((entry) => entry.family === family);

export const getTaperReference = (id) =>
  TAPER_REFERENCES.find((entry) => entry.id === id) || null;

export const resolveTaperGeometry = (params) => {
  const reference = getTaperReference(params.taperSize);
  const unitFactor = params.unit === "metric" ? INCH_TO_MM : 1;
  const referenceLength = reference?.referenceLengthIn ? reference.referenceLengthIn * unitFactor : 0;
  const enteredLength = Number(params.length);
  const length = enteredLength > 0 ? enteredLength : referenceLength;
  const overrideLarge = Number(params.largeDiameter);
  const overrideSmall = Number(params.smallDiameter);
  let largeDiameter = overrideLarge > 0 ? overrideLarge : (reference?.largeDiameterIn || 0) * unitFactor;
  let smallDiameter = overrideSmall > 0 ? overrideSmall : (reference?.smallDiameterIn || 0) * unitFactor;
  const taperPerUnit = reference?.taperPerInch || 0;

  if (taperPerUnit > 0 && length > 0) {
    if (largeDiameter > 0 && !(smallDiameter > 0)) smallDiameter = largeDiameter - (taperPerUnit * length);
    if (smallDiameter > 0 && !(largeDiameter > 0)) largeDiameter = smallDiameter + (taperPerUnit * length);
  }

  return {
    reference,
    length,
    largeDiameter,
    smallDiameter,
    referenceLength,
  };
};
